import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Commission rates (2026 CMS FMV at 90% FMO)
// 2026 FMO Contracted Rates (flat — no state-based variation)
// These MUST match backend PolicyController + CommissionEngineService
const COMMISSION_RATES = {
  MA: {
    initial: 694.00,  // New to Medicare / New to MA — flat, never pro-rated
    renewal: 347.00,  // Plan Switch / Renewal — pro-rated by effective month
  },
  PDP: {
    initial: 103.00,
    renewal: 51.00,
  }
};

// Ancillary commission rates (flat rates per product)
// NOTE: Agent payouts set to $0 until commission structure is finalized
// Update agentPayout values once determined - can be done post-production
const ANCILLARY_COMMISSION_RATES: Record<string, { initial: number; renewal: number; agentPayout: number }> = {
  HI: { initial: 0, renewal: 0, agentPayout: 0 },      // Hospital Indemnity - TBD
  DENTAL: { initial: 0, renewal: 0, agentPayout: 0 },  // Dental - TBD
  CANCER: { initial: 0, renewal: 0, agentPayout: 0 },  // Cancer - TBD
  HS: { initial: 0, renewal: 0, agentPayout: 0 },      // Heart/Stroke - TBD
  ACCIDENT: { initial: 0, renewal: 0, agentPayout: 0 },// Accident - TBD
  VISION: { initial: 0, renewal: 0, agentPayout: 0 }   // Vision - TBD
};

// Map ancillary type IDs to product types
const ANCILLARY_TYPE_TO_PRODUCT: Record<string, string> = {
  hospital_indemnity: 'HI',
  dental: 'DENTAL',
  cancer: 'CANCER',
  heart_stroke: 'HS',
  accident: 'ACCIDENT',
  vision: 'VISION'
};

// State-based rate variation removed — 2026 FMO rates are flat national

const ENROLLMENT_TYPES: Record<string, { rateType: string; proRated: boolean }> = {
  NEW_TO_MEDICARE: { rateType: 'initial', proRated: false },
  NEW_TO_MA: { rateType: 'initial', proRated: true },
  PLAN_SWITCH: { rateType: 'renewal', proRated: true },
  RENEWAL: { rateType: 'renewal', proRated: false }
};

// Agent payout per deal
const AGENT_PAYOUT_PER_DEAL = 50;

// Calculate commission
const calculateCommission = (
  enrollmentType: string,
  productType: string,
  state: string,
  effectiveDate: string
) => {
  const config = ENROLLMENT_TYPES[enrollmentType];
  if (!config) return { amount: 0, isProRated: false, monthsRemaining: 12, agentPayout: AGENT_PAYOUT_PER_DEAL };

  const product = productType === 'PDP' ? 'PDP' : 'MA';
  const rates = COMMISSION_RATES[product as keyof typeof COMMISSION_RATES];

  // Flat rate lookup — no state-based variation (2026 FMO contracted)
  const baseRate = config.rateType === 'initial' ? rates.initial : rates.renewal;

  if (config.proRated && effectiveDate) {
    const date = new Date(effectiveDate);
    const effectiveMonth = date.getMonth();
    const monthsRemaining = 12 - effectiveMonth;
    return {
      amount: Math.round((baseRate / 12) * monthsRemaining),
      isProRated: true,
      monthsRemaining,
      fullAmount: baseRate,
      agentPayout: AGENT_PAYOUT_PER_DEAL
    };
  }

  return {
    amount: baseRate,
    isProRated: false,
    monthsRemaining: 12,
    fullAmount: baseRate,
    agentPayout: AGENT_PAYOUT_PER_DEAL
  };
};

// Generate policy ID
const generatePolicyId = (existingPolicies: any[]) => {
  const year = new Date().getFullYear();
  const existingThisYear = existingPolicies.filter(p => p.id?.includes(`POL-${year}`));
  const nextNum = existingThisYear.length + 1;
  return `POL-${year}-${String(nextNum).padStart(5, '0')}`;
};

// Create policy from enrolled contact
const createPolicyFromContact = (contact: any, existingPolicies: any[], currentUser: any) => {
  const commission = calculateCommission(
    contact.enrollmentType,
    contact.product,
    contact.state,
    contact.effectiveDate
  );

  const now = new Date().toISOString();

  return {
    id: generatePolicyId(existingPolicies),
    contactId: contact.id,
    clientName: `${contact.firstName} ${contact.lastName}`,
    clientMBI: contact.mbiNumber,
    clientState: contact.state,
    clientCounty: contact.county,
    enrollmentType: contact.enrollmentType || 'NEW_TO_MEDICARE',
    carrier: contact.carrier,
    planType: contact.planType,
    planName: contact.planName,
    productType: contact.product || 'MA',
    effectiveDate: contact.effectiveDate,
    confirmationCode: contact.confirmationCode,
    commissionAmount: commission.amount,
    agentPayout: commission.agentPayout,
    commissionType: ENROLLMENT_TYPES[contact.enrollmentType]?.rateType || 'initial',
    isProRated: commission.isProRated,
    monthsRemaining: commission.monthsRemaining,
    fullCommissionAmount: commission.fullAmount,
    status: 'ACTIVE',
    statementVerified: false,
    statementVerifiedAt: null,
    statementId: null,
    replacedPolicyId: null,
    replacedByPolicyId: null,
    originalPolicyId: null,
    policyYear: 1,
    retentionAttempts: [],
    voidedAt: null,
    voidedBy: null,
    voidReason: null,
    voidNotes: null,
    terminatedAt: null,
    terminationReason: null,
    terminationNotes: null,
    createdAt: now,
    createdBy: currentUser?.id || 'unknown',
    createdByName: currentUser?.name || 'Unknown User',
    updatedAt: now,
    updatedBy: currentUser?.id || 'unknown',
    agentId: contact.agentId || currentUser?.id,
    agentName: contact.agentName || currentUser?.name,
    history: [
      {
        action: 'CREATED',
        timestamp: now,
        userId: currentUser?.id || 'unknown',
        userName: currentUser?.name || 'Unknown User',
        details: {
          enrollmentType: contact.enrollmentType,
          carrier: contact.carrier,
          planName: contact.planName,
          confirmationCode: contact.confirmationCode,
          commissionAmount: commission.amount,
          agentPayout: commission.agentPayout
        }
      }
    ]
  };
};

// Create ancillary policy from upsell
export const createAncillaryPolicy = (
  ancillary: {
    id: string;
    type: string;
    carrier: string;
    coverageAmount?: string;
    premium?: string;
    status: string;
    createdAt: string;
  },
  contact: any,
  parentPolicyId: string | null,
  existingPolicies: any[],
  currentUser: any
) => {
  const productType = ANCILLARY_TYPE_TO_PRODUCT[ancillary.type] || 'HI';
  const rates = ANCILLARY_COMMISSION_RATES[productType] || ANCILLARY_COMMISSION_RATES.HI;
  const now = new Date().toISOString();

  // Product display names
  const PRODUCT_NAMES: Record<string, string> = {
    HI: 'Hospital Indemnity',
    DENTAL: 'Dental',
    CANCER: 'Cancer',
    HS: 'Heart/Stroke',
    ACCIDENT: 'Accident',
    VISION: 'Vision'
  };

  // Carrier display names
  const CARRIER_NAMES: Record<string, string> = {
    heartland: 'Heartland',
    uh1: 'UH1',
    aflac: 'Aflac',
    mutual_omaha: 'Mutual of Omaha',
    other: 'Other'
  };

  return {
    id: generatePolicyId(existingPolicies),
    contactId: contact.id,
    clientName: `${contact.firstName || contact.first_name || ''} ${contact.lastName || contact.last_name || ''}`.trim(),
    clientMBI: contact.mbiNumber,
    clientState: contact.state,
    clientCounty: contact.county,
    enrollmentType: 'NEW_TO_MEDICARE', // Ancillaries default to new
    carrier: CARRIER_NAMES[ancillary.carrier] || ancillary.carrier,
    planType: productType,
    planName: PRODUCT_NAMES[productType] || 'Ancillary',
    productType,
    effectiveDate: contact.effectiveDate || new Date().toISOString().split('T')[0],
    confirmationCode: `ANC-${ancillary.id}`,
    commissionAmount: rates.initial,
    agentPayout: rates.agentPayout,
    commissionType: 'initial',
    isProRated: false,
    monthsRemaining: 12,
    fullCommissionAmount: rates.initial,
    status: 'ACTIVE',
    statementVerified: false,
    statementVerifiedAt: null,
    statementId: null,
    replacedPolicyId: null,
    replacedByPolicyId: null,
    originalPolicyId: null,
    parentPolicyId, // Link to parent Medicare policy
    policyYear: 1,
    retentionAttempts: [],
    voidedAt: null,
    voidedBy: null,
    voidReason: null,
    voidNotes: null,
    terminatedAt: null,
    terminationReason: null,
    terminationNotes: null,
    createdAt: now,
    createdBy: currentUser?.id || 'unknown',
    createdByName: currentUser?.name || 'Unknown User',
    updatedAt: now,
    updatedBy: currentUser?.id || 'unknown',
    agentId: contact.agentId || currentUser?.id,
    agentName: contact.agentName || currentUser?.name,
    coverageAmount: ancillary.coverageAmount,
    premium: ancillary.premium,
    history: [
      {
        action: 'CREATED',
        timestamp: now,
        userId: currentUser?.id || 'unknown',
        userName: currentUser?.name || 'Unknown User',
        details: {
          type: 'ANCILLARY_UPSELL',
          productType,
          carrier: ancillary.carrier,
          parentPolicyId,
          commissionAmount: rates.initial,
          agentPayout: rates.agentPayout
        }
      }
    ]
  };
};

export interface Contact {
  id: string;
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
  dateOfBirth?: string;
  status?: string;
  product?: string;
  products?: string[];
  source?: string;
  premium?: number;
  pipelineStage?: string;
  // Medicare-specific
  mbiNumber?: string;
  partADate?: string;
  partBDate?: string;
  currentCoverage?: string;
  carrier?: string;
  planType?: string;
  planName?: string;
  effectiveDate?: string;
  enrollmentType?: string;
  confirmationCode?: string;
  disposition?: string;
  dispositionName?: string;
  dispositionDate?: string;
  // Agent info
  agentId?: string;
  agentName?: string;
  assignedTo?: string;
  createdBy?: string;
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

export interface Policy {
  id: string;
  contactId: string;
  clientName: string;
  clientMBI?: string;
  clientState?: string;
  clientCounty?: string;
  enrollmentType: string;
  carrier?: string;
  planType?: string;
  planName?: string;
  productType: string;
  effectiveDate?: string;
  confirmationCode?: string;
  commissionAmount: number;
  agentPayout: number;
  commissionType: string;
  isProRated: boolean;
  monthsRemaining: number;
  fullCommissionAmount?: number;
  status: string;
  statementVerified: boolean;
  agentId?: string;
  agentName?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  history?: any[];
}

export interface Task {
  id: string;
  title: string;
  type: string;
  priority: string;
  status: string;
  contactId?: string;
  dueDate?: string;
  dueTime?: string;
  notes?: string;
  completedAt?: string;
  createdAt?: string;
  // Agent assignment fields
  assignedTo?: string;
  createdBy?: string;
  agent?: string;
}

interface RecentSale {
  clientName: string;
  productType: string;
  commission: number;
  agentPayout: number;
  policyId: string;
}

interface ContactsState {
  // Data
  contacts: Contact[];
  policies: Policy[];
  tasks: Task[];

  // UI State
  viewingContact: Contact | null;
  selectedContact: Contact | null;
  showNewContactModal: boolean;
  recentSale: RecentSale | null;
  isLoading: boolean;
  error: string | null;

  // Data Actions
  setContacts: (contacts: Contact[]) => void;
  addContact: (contact: Contact, currentUser: any) => Promise<void>;
  updateContact: (contact: Contact, currentUser: any) => void | Promise<void>;
  deleteContact: (contactId: string) => Promise<void>;

  setPolicies: (policies: Policy[]) => void;
  addPolicy: (policy: Policy) => void;
  updatePolicy: (policy: Policy) => void;
  voidPolicy: (policyId: string, reason: string, notes: string, currentUser: any) => void | Promise<void>;

  setTasks: (tasks: Task[]) => void;
  addTask: (task: Task) => void;
  updateTask: (task: Task) => void;
  deleteTask: (taskId: string) => void;

  // UI Actions
  setViewingContact: (contact: Contact | null) => void;
  setSelectedContact: (contact: Contact | null) => void;
  openNewContactModal: () => void;
  closeNewContactModal: () => void;

  // Enrollment Actions
  handleContactDisposition: (contact: Contact, currentUser: any) => void | Promise<void>;

  // Recent sale
  setRecentSale: (sale: RecentSale | null) => void;
  clearRecentSale: () => void;

  // Fetch
  fetchContacts: () => Promise<void>;

  // Loading
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Helpers
  getContactById: (id: string) => Contact | undefined;
  getPolicyById: (id: string) => Policy | undefined;
  getActivePolicies: () => Policy[];
  getTodaysSales: () => Policy[];
  getAgentPolicies: (agentId: string) => Policy[];
}

export const useContactsStore = create<ContactsState>()(
  persist(
    (set, get) => ({
      contacts: [],
      policies: [],
      tasks: [],
      viewingContact: null,
      selectedContact: null,
      showNewContactModal: false,
      recentSale: null,
      isLoading: false,
      error: null,

      fetchContacts: async () => {
        // Don't load all contacts into memory — individual pages use server-side pagination
        // This store fetch loads a lightweight batch for dropdowns, search suggestions, etc.
        set({ isLoading: true, error: null });
        try {
          const token = typeof window !== 'undefined' ? localStorage.getItem('alvo-token') : null;
          const headers: Record<string, string> = { 'Accept': 'application/json' };
          if (token) headers['Authorization'] = 'Bearer ' + token;
          
          // Load just the first page for basic store usage (dropdowns, recent contacts)
          const resp = await fetch('/api/v1/contacts?per_page=100&page=1', { headers, credentials: 'omit' });
          if (!resp.ok) throw new Error('Failed to fetch contacts: ' + resp.status);
          const res = await resp.json();
          const data = res?.data || [];
          const mapped = data.map((c: any) => ({
            id: c.id,
            firstName: c.first_name,
            lastName: c.last_name,
            name: c.name,
            email: c.email,
            phone: c.phone,
            address: c.address,
            city: c.city,
            state: c.state,
            zipCode: c.zip_code,
            county: c.county,
            dateOfBirth: c.date_of_birth,
            status: c.status,
            pipelineStage: c.pipeline_stage,
            product: c.product,
            products: c.products,
            source: c.source,
            premium: c.premium ? parseFloat(c.premium) : undefined,
            mbiNumber: c.mbi_number,
            partADate: c.part_a_date,
            partBDate: c.part_b_date,
            currentCoverage: c.current_coverage,
            carrier: c.carrier,
            planType: c.plan_type,
            planName: c.plan_name,
            effectiveDate: c.effective_date,
            enrollmentType: c.enrollment_type,
            confirmationCode: c.confirmation_code,
            disposition: c.disposition,
            dispositionName: c.disposition_name,
            dispositionDate: c.disposition_date,
            agentId: c.agent_id,
            agentName: c.agent_name,
            assignedTo: c.assigned_to,
            createdBy: c.created_by,
            createdAt: c.created_at,
            updatedAt: c.updated_at,
          }));
          set({ contacts: mapped, isLoading: false });
        } catch (err: any) {
          console.error('[fetchContacts] Failed:', err);
          set({ error: err?.message || 'Failed to fetch contacts', isLoading: false });
        }
      },

      setContacts: (contacts) => set({ contacts }),

      addContact: async (contact, currentUser) => {
        const tempId = contact.id || crypto.randomUUID();
        const newContact = {
          ...contact,
          id: tempId,
          createdAt: contact.createdAt || new Date().toISOString(),
          agentId: contact.agentId || currentUser?.id,
          agentName: contact.agentName || currentUser?.name
        };

        // Add to store immediately for responsiveness
        set((state) => ({
          contacts: [newContact, ...state.contacts]
        }));

        // If Medicare product, open the detail view
        const isMedicare = ['MA', 'MS', 'PDP'].includes(contact.product || '');
        if (isMedicare) {
          set({ viewingContact: newContact, showNewContactModal: false });
        }

        // Persist to backend — replace temp ID with server UUID
        try {
          const { default: api } = await import('@alvo/api');
          // Convert camelCase to snake_case for Laravel
          const payload: Record<string, any> = {
            first_name: newContact.firstName || '',
            last_name: newContact.lastName || '',
            email: newContact.email || null,
            phone: newContact.phone || null,
            date_of_birth: newContact.dateOfBirth || null,
            mbi_number: newContact.mbiNumber || null,
            address: newContact.address || null,
            city: newContact.city || null,
            state: newContact.state || null,
            county: newContact.county || null,
            zip_code: newContact.zipCode || null,
            source: newContact.source || null,
            notes: newContact.notes || null,
            product: newContact.product || null,
            pipeline_stage: newContact.pipelineStage || 'NEW',
            status: newContact.status || 'LEAD',
            premium: newContact.premium || null,
            carrier: newContact.carrier || null,
            plan_type: newContact.planType || null,
            plan_name: newContact.planName || null,
            effective_date: newContact.effectiveDate || null,
            enrollment_type: newContact.enrollmentType || null,
            confirmation_code: newContact.confirmationCode || null,
            current_coverage: newContact.currentCoverage || null,
            part_a_date: newContact.partADate || null,
            part_b_date: newContact.partBDate || null,
          };
          const response = await api.contacts.create(payload as any);
          const saved = (response as any)?.data || response;
          if (saved?.id && saved.id !== tempId) {
            set((state) => ({
              contacts: state.contacts.map((c) =>
                c.id === tempId ? { ...c, id: saved.id } : c
              ),
              viewingContact: state.viewingContact?.id === tempId
                ? { ...state.viewingContact, id: saved.id }
                : state.viewingContact
            }));
          }
        } catch (err) {
          console.warn('[addContact] Backend save failed, contact exists locally only:', err);
        }
      },

      updateContact: async (contact, _currentUser) => {
        set((state) => ({
          contacts: state.contacts.map((c) =>
            c.id === contact.id ? { ...contact, updatedAt: new Date().toISOString() } : c
          ),
          viewingContact: state.viewingContact?.id === contact.id ? contact : state.viewingContact
        }));

        // Persist to backend
        if (contact.id) {
          try {
            const { default: api } = await import('@alvo/api');
            const payload: Record<string, any> = {
              first_name: contact.firstName || '',
              last_name: contact.lastName || '',
              email: contact.email || null,
              phone: contact.phone || null,
              date_of_birth: contact.dateOfBirth || null,
              mbi_number: contact.mbiNumber || null,
              address: contact.address || null,
              city: contact.city || null,
              state: contact.state || null,
              county: contact.county || null,
              zip_code: contact.zipCode || null,
              source: contact.source || null,
              notes: contact.notes || null,
              product: contact.product || null,
              pipeline_stage: contact.pipelineStage || undefined,
              status: contact.status || undefined,
              premium: contact.premium || null,
              carrier: contact.carrier || null,
              plan_type: contact.planType || null,
              plan_name: contact.planName || null,
              effective_date: contact.effectiveDate || null,
              enrollment_type: contact.enrollmentType || null,
              confirmation_code: contact.confirmationCode || null,
              current_coverage: contact.currentCoverage || null,
              part_a_date: contact.partADate || null,
              part_b_date: contact.partBDate || null,
              disposition: contact.disposition || null,
              disposition_name: contact.dispositionName || null,
            };
            // Remove undefined values
            Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
            await api.contacts.update(contact.id, payload);
            console.log('\u2705 Contact updated on backend:', contact.id);
          } catch (err) {
            console.error('\u274c Failed to update contact on backend:', err);
          }
        }
      },

      deleteContact: async (contactId) => {
        console.log('[Store] deleteContact called with:', contactId);
        // Optimistic: remove locally first
        set((state) => ({
          contacts: state.contacts.filter((c) => c.id !== contactId)
        }));
        // Persist to backend
        try {
          const { contactsApi } = await import('@alvo/api');
          await contactsApi.delete(contactId);
        } catch (err) {
          console.error('[Store] Failed to delete contact on backend:', err);
          // Re-add contact to local state since backend delete failed
          const { contacts: currentContacts } = get();
          if (!currentContacts.find(c => c.id === contactId)) {
            // Contact was already removed from local state - fetch fresh data
            get().fetchContacts();
          }
        }
      },

      setPolicies: (policies) => set({ policies }),

      addPolicy: (policy) => {
        set((state) => ({
          policies: [policy, ...state.policies]
        }));
      },

      updatePolicy: (policy) => {
        set((state) => ({
          policies: state.policies.map((p) =>
            p.id === policy.id ? { ...policy, updatedAt: new Date().toISOString() } : p
          )
        }));
      },

      voidPolicy: async (policyId, reason, notes, currentUser) => {
        const now = new Date().toISOString();
        // Optimistic update — void locally first
        set((state) => ({
          policies: state.policies.map((p) => {
            if (p.id === policyId) {
              return {
                ...p,
                status: 'VOIDED',
                voidedAt: now,
                voidedBy: currentUser?.id || 'unknown',
                voidReason: reason,
                voidNotes: notes,
                updatedAt: now,
                history: [
                  ...(p.history || []),
                  {
                    action: 'VOIDED',
                    timestamp: now,
                    userId: currentUser?.id || 'unknown',
                    userName: currentUser?.name || 'Unknown User',
                    details: { reason, notes }
                  }
                ]
              };
            }
            return p;
          })
        }));

        // Persist void to backend — triggers chargeback + clawback in commission engine
        try {
          const { default: api } = await import('@alvo/api');
          const resp = await api.policies.void(policyId, { reason, notes });
          console.log('✅ Policy voided on backend:', policyId, resp);
        } catch (err) {
          console.error('❌ Failed to void policy on backend:', err);
          // Policy remains voided locally — admin can reconcile
        }
      },

      setTasks: (tasks) => set({ tasks }),

      addTask: (task) => {
        set((state) => ({
          tasks: [{ ...task, id: task.id || crypto.randomUUID() }, ...state.tasks]
        }));
      },

      updateTask: (task) => {
        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === task.id ? task : t))
        }));
      },

      deleteTask: (taskId) => {
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== taskId)
        }));
      },

      setViewingContact: (contact) => set({ viewingContact: contact }),
      setSelectedContact: (contact) => set({ selectedContact: contact }),
      openNewContactModal: () => set({ showNewContactModal: true }),
      closeNewContactModal: () => set({ showNewContactModal: false }),

      // Handle disposition - this is the key enrollment flow
      handleContactDisposition: async (contact, currentUser) => {
        const { policies } = get();

        // Update the contact
        set((state) => ({
          contacts: state.contacts.map((c) =>
            c.id === contact.id ? { ...contact, updatedAt: new Date().toISOString() } : c
          )
        }));

        // Check if newly enrolled with required data
        const isNewlyEnrolled =
          contact.pipelineStage === 'ENROLLED' &&
          contact.confirmationCode &&
          contact.enrollmentType;

        // Check if policy already exists
        const policyExists = policies.some(
          (p) => p.confirmationCode === contact.confirmationCode
        );

        console.log('[Enrollment Check]', {
          pipelineStage: contact.pipelineStage,
          confirmationCode: contact.confirmationCode,
          enrollmentType: contact.enrollmentType,
          isNewlyEnrolled,
          policyExists,
          existingPolicies: policies.length
        });

        if (isNewlyEnrolled && !policyExists) {
          // Create new policy
          const newPolicy = createPolicyFromContact(contact, policies, currentUser);

          console.log('🎉 New policy created:', newPolicy.id, newPolicy.clientName, newPolicy.commissionAmount);

          set((state) => ({
            policies: [newPolicy, ...state.policies],
            recentSale: {
              clientName: newPolicy.clientName,
              productType: newPolicy.productType,
              commission: newPolicy.commissionAmount,
              agentPayout: newPolicy.agentPayout,
              policyId: newPolicy.id
            }
          }));

          // Persist policy to backend
          try {
            const { default: api } = await import('@alvo/api');
            const apiPayload = {
              contact_id: newPolicy.contactId,
              enrollment_type: newPolicy.enrollmentType,
              carrier: newPolicy.carrier,
              plan_type: newPolicy.planType,
              plan_name: newPolicy.planName || newPolicy.carrier + ' Plan',
              product_type: newPolicy.productType,
              effective_date: newPolicy.effectiveDate,
              confirmation_code: newPolicy.confirmationCode,
              commission_amount: newPolicy.commissionAmount,
              agent_payout: newPolicy.agentPayout,
              premium: null,
            };
            const resp = await api.policies.create(apiPayload);
            const saved = (resp as any)?.data || resp;
            if (saved?.id && saved.id !== newPolicy.id) {
              // Update local policy with server-assigned ID
              set((state) => ({
                policies: state.policies.map((p) =>
                  p.id === newPolicy.id ? { ...p, id: saved.id, policyNumber: saved.policy_number || saved.policyNumber || p.id } : p
                ),
                recentSale: state.recentSale?.policyId === newPolicy.id
                  ? { ...state.recentSale, policyId: saved.id }
                  : state.recentSale
              }));
            }
            console.log('✅ Policy persisted to backend:', saved?.id || 'ok');
          } catch (err) {
            console.error('❌ Failed to persist policy to backend:', err);
            // Policy remains in local state — will show up but won't survive refresh
            // This is acceptable: agent sees the sale, admin can reconcile later
          }
        }

        // Close the detail view
        set({ viewingContact: null });
      },

      setRecentSale: (sale) => set({ recentSale: sale }),
      clearRecentSale: () => set({ recentSale: null }),

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      getContactById: (id) => get().contacts.find((c) => c.id === id),
      getPolicyById: (id) => get().policies.find((p) => p.id === id),
      getActivePolicies: () => get().policies.filter((p) => p.status === 'ACTIVE'),
      getTodaysSales: () => {
        const today = new Date().toDateString();
        return get().policies.filter((p) => {
          const createdDate = new Date(p.createdAt).toDateString();
          return createdDate === today && p.status === 'ACTIVE';
        });
      },
      getAgentPolicies: (agentId) =>
        get().policies.filter((p) => p.agentId === agentId)
    }),
    {
      name: 'alvo-contacts',
      partialize: (state) => ({
        // Don't persist large datasets — fetch fresh from API each session
        // policies excluded: 2000+ records exceeds localStorage 5MB limit
        tasks: state.tasks
      })
    }
  )
);

// Convenience hooks
export const useContacts = () => useContactsStore((state) => state.contacts);
export const usePolicies = () => useContactsStore((state) => state.policies);
export const useTasks = () => useContactsStore((state) => state.tasks);
export const useViewingContact = () => useContactsStore((state) => state.viewingContact);
export const useRecentSale = () => useContactsStore((state) => state.recentSale);
